import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Field, Heading, Label, Paragraph, Tag, Textarea } from '@digdir/designsystemet-react';
import type { ChecklistSuggestion } from '../../../../api/safetyRounds';
import { dayAndMonth } from '../../../WorkEnvironmentSurveyDetail/surveyProcessView';
import layout from '../../../HRDashboard/hmsPage.module.css';
import classes from '../Checklists.module.css';

type Props = {
  suggestions: ChecklistSuggestion[];
  onAccept: (id: number) => void;
  onReject: (id: number, comment: string) => void;
  failed: boolean;
};

/** Suggestions from safety representatives, taken into the draft or rejected with a reason */
export const SuggestionsCard = ({ suggestions, onAccept, onReject, failed }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const K = 'safetyRounds.checklist.suggestions';

  return (
    <section aria-labelledby="checklist-suggestions" className={layout.card} data-gap="md">
      <div className={layout.cardHeading}>
        <Heading level={2} data-size="sm" id="checklist-suggestions">
          {t(`${K}.title`)}
        </Heading>
        <Paragraph data-size="sm" className={layout.cardIntro}>
          {t(`${K}.intro`)}
        </Paragraph>
      </div>
      <ul className={classes.suggestions}>
        {suggestions.map((suggestion) => (
          <li key={suggestion.id} className={classes.suggestion}>
            <Paragraph data-size="sm">«{suggestion.text}»</Paragraph>
            <Paragraph data-size="sm" className={layout.subtle}>
              {[
                suggestion.fromEmail,
                dayAndMonth(suggestion.createdAt, i18n.language),
                suggestion.categoryName && t(`${K}.under`, { category: suggestion.categoryName }),
              ]
                .filter(Boolean)
                .join(' · ')}
            </Paragraph>
            {suggestion.status === 'NEW' ? (
              rejecting === suggestion.id ? (
                <>
                  <Field>
                    <Label htmlFor={`reject-${suggestion.id}`}>{t(`${K}.rejectComment`)}</Label>
                    <Textarea
                      id={`reject-${suggestion.id}`}
                      rows={2}
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                    />
                  </Field>
                  <div className={layout.actions}>
                    <Button
                      data-size="sm"
                      onClick={() => {
                        onReject(suggestion.id, comment);
                        setRejecting(null);
                        setComment('');
                      }}
                    >
                      {t(`${K}.confirmReject`)}
                    </Button>
                    <Button data-size="sm" variant="tertiary" onClick={() => setRejecting(null)}>
                      {t('safetyRounds.cancel')}
                    </Button>
                  </div>
                </>
              ) : (
                <div className={layout.actions}>
                  <Button data-size="sm" variant="secondary" onClick={() => onAccept(suggestion.id)}>
                    {t(`${K}.accept`)}
                  </Button>
                  <Button data-size="sm" variant="tertiary" onClick={() => setRejecting(suggestion.id)}>
                    {t(`${K}.reject`)}
                  </Button>
                </div>
              )
            ) : (
              <div>
                <Tag data-color={suggestion.status === 'ACCEPTED' ? 'success' : 'neutral'} data-size="sm">
                  {t(`${K}.status.${suggestion.status}`)}
                </Tag>
                {suggestion.comment && (
                  <Paragraph data-size="sm" className={layout.subtle}>
                    {suggestion.comment}
                  </Paragraph>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {failed && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
        </Alert>
      )}
    </section>
  );
};
